import { pushEvent } from 'helpers/utils/checkoutDataLayer';
import { events } from 'constants/dataLayer';
import { getCurrentLanguage } from 'helpers/vernacular';
import { PAGE_NAMES, PAGE_TYPES, TRACKING_EVENTS } from 'constants/index';


const pageName = 'addNewAddressPage';

interface TrackNewAddressPageLoadConfig {
  customerId: string;
  isGuest: boolean
  isFirstLandingPageAfterCheckout: boolean;
  isNewAddressDesignEnabled?: boolean;
}

interface IAddressPaymentBottomsheetLoads{
  interaction_location: string;
  payment_methods_shown: string[];
  payment_methods_recommended: string[];
  default_payment_method: string;
  cart_total_modified: number;
  cart_subtotal_modified: number;
  mrp_discount_modified: string;
  cart_display_amount_modified: number;
  cart_total_qty: number;
  cart_product_count: number;
}

export const trackNewAddressPageLoad = (params: TrackNewAddressPageLoadConfig) => {
  const {
    customerId,
    isGuest,
    isFirstLandingPageAfterCheckout,
    isNewAddressDesignEnabled
  } = params;
  pushEvent(events.NEW_ADDRESS_PAGE_LOAD, {
    pageName,
    event: 'addNewAddressPageLoad',
    customerId: customerId,
    loginStatus: isGuest ? 'guest' : 'logged in',
    is_logged_in: !isGuest,
    addNewAddressExperiment: 'Variant1',
    page: PAGE_NAMES.ADD_NEW_ADDRESS,
    page_type: PAGE_TYPES.ADDRESS_PAGE,
    add_address_location: PAGE_NAMES.SAVED_ADDRESS,
    interaction_location: PAGE_NAMES.SAVED_ADDRESS,
    pageType: pageName,
    isFirstLandingPageAfterCheckout: isFirstLandingPageAfterCheckout,
    isNewAddressDesignEnabled
  });
};

export const trackAutoDetectClick = (params: {pincode: string}) => {
  const {
    pincode,
  } = params;
  pushEvent(events.AUTO_DETECT_CLICK, {
    addressInteractions: 'locationDetect',
    pincode,
  });
};

export const trackAutoDetectCloseClick = (params: {pincode: string}) => {
  const {
      pincode,
  } = params;
  pushEvent(events.AUTO_DETECT_CONFIRM_CLICK, {
    addressInteractions: 'closeAutoDetectLocation',
    pincode,
  });
};

export const trackErrorContinueClick = (params: {errorMessage: string}) => {
  const {
    errorMessage,
  } = params;
  pushEvent(events.ERROR_CONTINUE_CLICK, {
    addressInteractions: 'continueOnAddNewAddress',
    errorMessage,
  });
};

export const trackUseDefaultAddressToggleClick = (params: {selectedToggleValue: string}) => {
  const {
    selectedToggleValue,
  } = params;
  pushEvent(events.USE_DEFAULT_ADDRESS_TOGGLE_CLICK, {
    addressInteractions: 'toggleMoved',
    selectedToggleValue,
  });
};

export const trackNewAddressShipToThisAddressClick = (params: { interactionLocation: string, isInputChanged: boolean }) => {
  const {
    interactionLocation,
    isInputChanged
  } = params;
  pushEvent(TRACKING_EVENTS.SHIP_TO_ADDRESS_CLICKS, {
    interactionLocation,
    isInputChanged
  });
};

export const trackNewAddressSaveAddressClick = (params: {pincode: string, isDefault: boolean}) => {
  const {
    pincode,
    isDefault,
  } = params;
  pushEvent(events.SAVE_ADDRESS_CLICK, {
    addressInteractions: 'newAddressSaveAddress',
    selectedAddressType: isDefault ? 'default' : 'other',
    pincode,
  });
};

export const trackLatinCharactersDetected = (params: {
  fieldName: string,
}) => {
  const {
    fieldName,
  } = params;
  pushEvent(events.LATIN_CHARACTERS_DETECTED, {
    pageName,
    nonLatinInput: 1,
    fieldName,
    lang: getCurrentLanguage(),
  });
};

export const trackAddNewAddressBackCliked = (params: {isPincodeFilled: boolean, isHouseDetailsFilled: boolean, isAreaDetailsFilled: boolean, isInputChanged: boolean, interactionLocation: string}) => {
  const {
    isPincodeFilled,
    isHouseDetailsFilled,
    isAreaDetailsFilled,
    isInputChanged,
    interactionLocation
  } = params;
  pushEvent(events.ADDRESS_BACK_BUTTON_CLICKED, {
    isPincodeFilled,
    isHouseDetailsFilled,
    isAreaDetailsFilled,
    isInputChanged,
    interactionLocation
  });
}

export const trackPincodeNonServiceable = (params: any) => {
  const {
    pincode
  } = params;
  pushEvent(events.PINCODE_NON_SERVICEABLE, {
    pincode
  });
};

export const trackShipToThisAddressClicksError = (params: any) => {
  const {
    errorType,
    interactionLocation
  } = params;
  pushEvent(events.SHIP_TO_ADDRESS_CLICKS_ERROR, {
    errorType,
    interactionLocation
  });
};

export const trackAddAddressBottomSheetLoads = (isImportAddressBottomSheet = false) => {
  pushEvent(events.ADD_ADDRESS_BOTTOM_SHEET_LOADS, {
    isImportAddressBottomSheet
  });
};

export const trackAddAddressBottomSheetClosed = () => {
  pushEvent(events.ADD_ADDRESS_BOTTOM_SHEET_CLOSED, {
  });
};

export const trackImportCtaClicked = (params: any) => {
  const {
    interaction_location
  } = params;
  pushEvent(events.IMPORT_CTA_CLICKED, {
    interaction_location
  });
};

export const trackImportAddressPageLoads = (params: any) => {
  const {
    no_of_address_present,
    interaction_location
  } = params;
  pushEvent(events.IMPORT_ADDRESS_PAGE_LOADS, {
    no_of_address_present,
    interaction_location
  });
};

export const trackImportAddressClicked = (params: any) => {
  const {
    no_of_address_present,
    no_of_address_selected,
    interaction_location
  } = params;
  pushEvent(events.IMPORT_ADDRESS_CLICKED, {
    no_of_address_present,
    no_of_address_selected,
    interaction_location
  });
};

export const trackAddressImported = (params: any) => {
  const {
    no_of_address_present,
    no_of_address_selected,
    no_of_address_synced,
    page_status,
    interaction_location
  } = params;
  pushEvent(events.ADDRESS_IMPORTED, {
    no_of_address_present,
    no_of_address_selected,
    no_of_address_synced,
    page_status,
    interaction_location
  });
};

export const trackAddressImportedSuccess = () => {
  pushEvent(events.ADDRESS_IMPORTED_SUCCESS, {
  });
};

export const trackAddAddressBottomSheetClicked = (params: any) => {
  const {
    interaction_location
  } = params;
  pushEvent(events.ADD_ADDRESS_BOTTOM_SHEET_CLICKED, {
    interaction_location
  });
};

export const trackAddressAutoDetectFailure = (params: any) => {
  const {
    error
  } = params;
  pushEvent(events.ADDRESS_AUTO_DETECT_FAILURE, {
    error
  });
};

// payment_bottomsheet_loads
export const trackAddressPaymentBottomsheetLoads = (params: IAddressPaymentBottomsheetLoads) => {
  pushEvent(events.PAYMENT_BOTTOMSHEET_LOADS, params);
};
